import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { AngularFireStorage } from '@angular/fire/storage';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface FeaApplication {
  name: string;
  email: string;
  phone?: string;
  message?: string;
  createdAt?: any;
}

@Injectable({
  providedIn: 'root'
})
export class FeaapplicationService {

  private readonly collection = 'feaApplications';

  constructor(private afs: AngularFirestore, private storage: AngularFireStorage) { }

  submitApplication(app: FeaApplication): Promise<string> {
    const data: FeaApplication = {
      ...app,
      createdAt: new Date()
    };
    return this.afs.collection(this.collection).add(data)
      .then(ref => ref.id)
      .catch(err => {
        console.error('Failed to submit application', err);
        throw err;
      });
  }

  getApplications(): Observable<FeaApplication[]> {
    return this.afs.collection<FeaApplication>(this.collection, ref => ref.orderBy('createdAt', 'desc')).valueChanges();
  }

  /**
   * Retrieve all FEA applications for users with the admin role.
   * Throws an Error when the caller is not an admin.
   * Returns an observable of application objects that include the document `id`.
   */
  getApplicationsForAdmin(role: string): Observable<(FeaApplication & { id: string })[]> {
    if (role !== 'admin') {
      throw new Error('Unauthorized: admin role required');
    }

    return this.afs.collection(this.collection, ref => ref.orderBy('createdAt', 'desc'))
      .snapshotChanges()
      .pipe(
        map(actions => actions.map((a: any) => {
          const data = a.payload.doc.data() as FeaApplication;
          const id = a.payload.doc.id;
          return { id, ...data };
        }))
      );
  }

  /**
   * Retrieve a single FEA application by document id for a student (learner) role.
   * Throws an Error when the caller is not a learner.
   */
  getApplicationByIdForLearner(id: string, role: string): Observable<FeaApplication | undefined> {
    if (role !== 'learner') {
      throw new Error('Unauthorized: learner role required');
    }

    return this.afs.doc<FeaApplication>(`${this.collection}/${id}`).valueChanges();
  }

  /** Update status of an application (accept/reject) - admin only
   *  Accepts optional reason and attachmentUrl in metadata
   */
  updateApplicationStatus(id: string, status: 'accepted' | 'rejected', metadata?: { reviewer?: string; reason?: string; attachmentUrl?: string; }): Promise<void> {
    const data: any = {
      status,
      reviewedAt: new Date()
    };
    if (metadata?.reviewer) { data.reviewer = metadata.reviewer; }
    if (metadata?.reason) { data.reviewReason = metadata.reason; }
    if (metadata?.attachmentUrl) { data.attachmentUrl = metadata.attachmentUrl; }
    return this.afs.doc(`${this.collection}/${id}`).update(data);
  }

  /** Convenience methods */
  acceptApplication(id: string, metadata?: { reviewer?: string; reason?: string; attachmentUrl?: string }): Promise<void> {
    return this.updateApplicationStatus(id, 'accepted', metadata);
  }

  rejectApplication(id: string, metadata?: { reviewer?: string; reason?: string; attachmentUrl?: string }): Promise<void> {
    return this.updateApplicationStatus(id, 'rejected', metadata);
  }

  /** Upload an attachment for an application and return the download URL */
  async uploadAttachment(id: string, file: File): Promise<string> {
    const path = `${this.collection}/${id}/${Date.now()}_${file.name}`;
    const ref = this.storage.ref(path);
    await this.storage.upload(path, file);
    return ref.getDownloadURL().toPromise();
  }
}
